import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { UsageService } from "@/services/usageService";
import { TenantService } from "@/services/tenantService";
import UsageSkeleton from "@/components/usage/UsageSkeleton";
import UsageStatBandSkeleton from "@/components/usage/UsageStatBandSkeleton";
import UsageToolbar from "@/components/usage/UsageToolbar";
import { readEngineeringDetailPreference, writeEngineeringDetailPreference } from "@/components/usage/engineeringDetailPreference";
import { useTenantUsage, windowParams } from "@/components/usage/useTenantUsage";
import { tenantName } from "@/components/usage/format";
import TenantScopePicker from "./components/TenantScopePicker";
import FleetOverview from "./components/FleetOverview";
import TenantUsageView from "./components/TenantUsageView";

/**
 * Platform usage — the fleet, or any one tenant in it.
 *
 * **One page, two scopes.** The picker in the toolbar switches between the
 * cross-tenant comparison and a single tenant's full report — the same report a
 * tenant admin reads of themselves, from the same components, plus the
 * engineering detail and the tenant's share of the fleet. Before this, seeing
 * one tenant's tools or people meant leaving for the tenant-details page, which
 * put the comparison and the explanation on two different screens.
 *
 * **The window and the depth survive the switch.** Scope, window and depth are
 * three independent questions, so changing one never resets another. The scope
 * also lives in the URL (`?tenant=<id>`), so a SuperAdmin can send someone the
 * exact view they are looking at.
 *
 * **The fleet call runs in both scopes.** It is one `GROUP BY tenant_id` on the
 * platform DB — no fan-out across dedicated tenant databases, which is exactly
 * why usage rows are stored platform-side. Keeping it live is what lets the
 * tenant view state a share of the fleet, and the picker rank its list.
 *
 * SuperAdmin pages gate on the client-side-only `super-admin.manage` constant,
 * as every other SuperAdmin page does; there is no backend permission code for
 * cross-tenant usage.
 */
const SuperAdminUsage = () => {
  const setHeader = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [range, setRange] = useState({ from: "", to: "" });
  const [detail, setDetail] = useState(readEngineeringDetailPreference);
  const [fleet, setFleet] = useState(null);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [allTenants, setAllTenants] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  const selectedTenantId = searchParams.get("tenant") || null;

  const selectTenant = (tenantId) => {
    setUserSearch("");
    setSearchParams(
      (params) => {
        if (tenantId) params.set("tenant", tenantId);
        else params.delete("tenant");
        return params;
      },
      { replace: true },
    );
  };

  const handleDetailChange = (next) => {
    writeEngineeringDetailPreference(next);
    setDetail(next);
  };

  // The full tenant list, once. It is what lets the picker offer a tenant with
  // no usage in the window — the very tenant somebody opens this page to check
  // on — which the usage rollup alone can never name.
  useEffect(() => {
    let cancelled = false;
    TenantService.fetchTenantList({ pagination: false, per_page: 200 })
      .then((response) => {
        if (cancelled) return;
        setAllTenants(response?.data?.tenants ?? []);
      })
      .catch(() => {
        // Not fatal. The picker falls back to the tenants that have usage, and
        // the rest of the page does not depend on this call at all.
        if (!cancelled) setAllTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setFleetLoading(true);
        const params = windowParams(range);
        if (detail) params.include_llm_detail = true;
        const result = await UsageService.getPlatformSummary(params);
        if (!cancelled) setFleet(result);
      } catch (error) {
        if (!cancelled) toast.error("Failed to load platform usage.");
      } finally {
        if (!cancelled) setFleetLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [range, detail]);

  const usageRows = useMemo(() => fleet?.tenants ?? [], [fleet]);
  const fleetTokens = Number(fleet?.totals?.totalTokens ?? 0);

  // One row per tenant, whether or not it consumed anything: the rollup carries
  // the numbers, the tenant list carries the names, the flags and the quiet
  // tenants. Ranked by tokens, so the picker opens on the heavy ones.
  //
  // A deleted tenant reaches this page through the rollup alone — usage rows
  // outlive the tenant, and the tenant list drops anything soft-deleted. That
  // is why the rollup's own `isActive` / `isDeleted` win below: for the deleted
  // tenant they are the only source, and for every other tenant they say the
  // same thing as the list. `??` keeps an older backend that sends neither.
  const pickerTenants = useMemo(() => {
    const byId = new Map();
    allTenants.forEach((tenant) => {
      byId.set(tenant.id, {
        id: tenant.id,
        name: tenant.name,
        slug: tenant.slug,
        dbMode: tenant.dbMode,
        isActive: tenant.isActive,
        isDeleted: false,
        uses: 0,
        totalTokens: 0,
      });
    });
    usageRows.forEach((row) => {
      const existing = byId.get(row.tenantId) ?? { id: row.tenantId, name: row.name, slug: row.slug };
      byId.set(row.tenantId, {
        ...existing,
        isActive: row.isActive ?? existing.isActive,
        isDeleted: row.isDeleted ?? existing.isDeleted,
        uses: Number(row.uses ?? 0),
        totalTokens: Number(row.totalTokens ?? 0),
      });
    });
    return [...byId.values()].sort((a, b) => b.totalTokens - a.totalTokens || String(a.name || a.slug || "").localeCompare(String(b.name || b.slug || "")));
  }, [allTenants, usageRows]);

  const selectedTenant = pickerTenants.find((t) => t.id === selectedTenantId) ?? null;

  const {
    data: tenantData,
    loading: tenantLoading,
    searchLoading,
  } = useTenantUsage({
    tenantId: selectedTenantId,
    range,
    detail,
    search: userSearch,
  });

  useEffect(() => {
    // Still "" while the tenant lists load, so the header does not flash a
    // stand-in name under a `?tenant=` URL that has not resolved yet.
    const name = selectedTenant ? tenantName(selectedTenant.name, selectedTenant.slug, selectedTenant.id) : "";
    setHeader?.({
      title: "Usage",
      subTitle: selectedTenantId ? `${name ? `${name} — ` : ""}tools, tokens and people` : "Consumption across every tenant",
    });
    return () => setHeader?.({ title: "", subTitle: "" });
  }, [setHeader, selectedTenantId, selectedTenant]);

  const busy = selectedTenantId ? tenantLoading : fleetLoading;

  return (
    <PermissionGuard permissions={[PERMISSIONS.SUPER_ADMIN_MANAGE]}>
      <div className="flex w-full flex-col gap-4 pb-4">
        {/* Sticky, because every card below it answers a question these three
            controls asked. Scrolling into the tenant table used to leave the
            reader with no reminder of which window they were reading. */}
        <UsageToolbar
          sticky
          range={range}
          onRangeChange={setRange}
          disabled={busy}
          detail={detail}
          onDetailChange={handleDetailChange}
          leading={<TenantScopePicker tenants={pickerTenants} value={selectedTenantId} onChange={selectTenant} fleetTokens={fleetTokens} disabled={busy} />}
        />

        {selectedTenantId ? (
          <TenantUsageView
            tenant={selectedTenant}
            data={tenantData}
            loading={tenantLoading}
            searchLoading={searchLoading}
            search={userSearch}
            onSearchChange={setUserSearch}
            fleetTokens={fleetTokens}
            onBack={() => selectTenant(null)}
          />
        ) : fleetLoading && !fleet ? (
          <div className="flex flex-col gap-4">
            <UsageStatBandSkeleton />
            <UsageSkeleton className="h-72 w-full" />
            <UsageSkeleton className="h-64 w-full" />
          </div>
        ) : (
          // Dimmed, not replaced, while a new window loads: the same numbers in
          // a slightly different window are a refresh, and a skeleton in their
          // place would lose the reader's scroll position and their comparison.
          <div className={`flex flex-col gap-4 transition-opacity ${fleetLoading ? "opacity-70" : ""}`}>
            <FleetOverview tenants={usageRows} totals={fleet?.totals} totalUses={fleet?.totalUses} callDetail={fleet?.totalsCallDetail} onSelectTenant={selectTenant} />
          </div>
        )}
      </div>
    </PermissionGuard>
  );
};

export default SuperAdminUsage;
